/**
 * checkRiskViolations
 *
 * Evaluates a user's trades against their configured risk settings.
 *
 * @param {Array}  trades       - All saved trades for the user
 * @param {Object} riskSettings - User's risk settings from Firestore
 * @param {Object|null} newTrade - Optional trade being added (not yet saved)
 * @returns {Array} Array of violation objects (empty = no violations)
 */
export const checkRiskViolations = (trades, riskSettings, newTrade = null) => {
  if (!riskSettings) return [];

  const violations = [];
  const today = new Date().toISOString().split('T')[0];

  // Today's saved trades + optional new trade
  const todayTrades = trades.filter(t => t.date === today);
  const allTodayTrades = newTrade ? [...todayTrades, newTrade] : todayTrades;

  // All trades (sorted oldest → newest) including optional new trade
  const allTrades = newTrade ? [...trades, newTrade] : trades;

  // ── 1. Daily Loss Limit ──────────────────────────────────────────────────
  if (riskSettings.dailyLossLimit > 0 && allTodayTrades.length > 0) {
    const todayPnL = allTodayTrades.reduce((sum, t) => sum + (t.pnl || 0), 0);
    if (todayPnL <= -riskSettings.dailyLossLimit) {
      violations.push({
        rule: 'daily_loss',
        title: 'Daily Loss Limit Reached!',
        message: `You have lost ₹${Math.abs(todayPnL).toFixed(0)} today. Stop trading for today.`,
        actual: Math.abs(todayPnL),
        limit: riskSettings.dailyLossLimit,
        limitLabel: 'Daily Loss Limit',
        unit: null
      });
    }
  }

  // ── 2. Max Trades Per Day ────────────────────────────────────────────────
  if (riskSettings.maxTradesPerDay > 0 && allTodayTrades.length >= riskSettings.maxTradesPerDay) {
    violations.push({
      rule: 'max_trades',
      title: 'Max Trades Per Day Reached!',
      message: `You have taken ${allTodayTrades.length} trades today. Stop trading for today.`,
      actual: allTodayTrades.length,
      limit: riskSettings.maxTradesPerDay,
      limitLabel: 'Daily Trade Limit',
      unit: 'trades'
    });
  }

  // ── 3. Consecutive Losses ────────────────────────────────────────────────
  if (riskSettings.maxConsecutiveLosses > 0 && allTrades.length > 0) {
    let consecutiveLosses = 0;
    for (let i = allTrades.length - 1; i >= 0; i--) {
      if ((allTrades[i].pnl || 0) < 0) consecutiveLosses++;
      else break;
    }
    if (consecutiveLosses >= riskSettings.maxConsecutiveLosses) {
      violations.push({
        rule: 'consecutive_losses',
        title: 'Consecutive Loss Alert!',
        message: `You have ${consecutiveLosses} losses in a row. Take a break before your next trade.`,
        actual: consecutiveLosses,
        limit: riskSettings.maxConsecutiveLosses,
        limitLabel: 'Max Consecutive Losses',
        unit: 'losses'
      });
    }
  }

  // ── 4. Per Trade Loss (only applies when checking a new trade) ───────────
  if (newTrade && riskSettings.maxSingleTradeLoss > 0 && (newTrade.pnl || 0) < 0) {
    const tradeLoss = Math.abs(newTrade.pnl);
    if (tradeLoss > riskSettings.maxSingleTradeLoss) {
      violations.push({
        rule: 'per_trade_risk',
        title: 'Per Trade Risk Exceeded!',
        message: `This trade would lose ₹${tradeLoss.toFixed(0)}. Your single-trade loss limit is ₹${riskSettings.maxSingleTradeLoss}.`,
        actual: tradeLoss,
        limit: riskSettings.maxSingleTradeLoss,
        limitLabel: 'Per Trade Loss Limit',
        unit: null
      });
    }
  }

  return violations;
};
